import { db } from '../db/db.js';
import type { InventoryEvent } from '../events/types.js';
import { rebuildProjections } from '../events/projector.js';
import type { UserSettings } from '../db/schema.js';
import { DEFAULT_USER_SETTINGS } from '../db/schema.js';

/**
 * Versioned backup file (v0.1.1 patch — JSON export/import for備份/還原).
 *
 * Strategy:
 *   - events[] is the source of truth (ADR-013); everything else is projectable.
 *   - settings is exported separately because v0.1 settings live outside the event log
 *     (tech debt: see SESSIONS log; v0.4 sync 時改 event-driven).
 *   - On import: clear events + projected tables + settings → write events from file
 *     → write settings → run rebuildProjections() to regenerate projected tables.
 */

export interface BackupFile {
  schemaVersion: 1;
  exportedAt: number;
  appVersion: string;
  events: InventoryEvent[];
  settings: UserSettings;
}

export const BACKUP_SCHEMA_VERSION = 1;

export async function exportBackup(appVersion: string): Promise<BackupFile> {
  const [events, settingsRow] = await Promise.all([
    db.events.orderBy('id').toArray(),
    db.settings.get('singleton'),
  ]);
  return {
    schemaVersion: BACKUP_SCHEMA_VERSION,
    exportedAt: Date.now(),
    appVersion,
    events,
    settings: settingsRow ?? DEFAULT_USER_SETTINGS,
  };
}

export async function exportBackupBlob(appVersion: string): Promise<{ blob: Blob; filename: string }> {
  const data = await exportBackup(appVersion);
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const stamp = new Date(data.exportedAt).toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return { blob, filename: `my-little-hamster-${stamp}.json` };
}

export interface ImportResult {
  schemaVersion: number;
  eventsImported: number;
  warnings: string[];
}

export async function importBackup(file: BackupFile): Promise<ImportResult> {
  validateBackup(file);
  const warnings: string[] = [];
  if (file.schemaVersion !== BACKUP_SCHEMA_VERSION) {
    warnings.push(`Backup schema v${file.schemaVersion} differs from current v${BACKUP_SCHEMA_VERSION}; trying anyway.`);
  }

  await db.transaction(
    'rw',
    [db.events, db.items, db.shoppingItems, db.subscriptions, db.settings],
    async () => {
      await db.events.clear();
      await db.items.clear();
      await db.shoppingItems.clear();
      await db.subscriptions.clear();
      await db.settings.clear();

      if (file.events.length > 0) {
        await db.events.bulkAdd(file.events);
      }
      await db.settings.put({ ...DEFAULT_USER_SETTINGS, ...file.settings, id: 'singleton' });
    },
  );

  await rebuildProjections();

  return {
    schemaVersion: file.schemaVersion,
    eventsImported: file.events.length,
    warnings,
  };
}

export async function importBackupFromText(text: string): Promise<ImportResult> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new Error('檔案不是合法的 JSON');
  }
  if (!isBackupFile(parsed)) {
    throw new Error('檔案結構不符（缺 schemaVersion / events / settings）');
  }
  return importBackup(parsed);
}

function validateBackup(file: BackupFile): void {
  if (typeof file.schemaVersion !== 'number') {
    throw new Error('Invalid backup: missing schemaVersion');
  }
  if (!Array.isArray(file.events)) {
    throw new Error('Invalid backup: events is not an array');
  }
  if (!file.settings || typeof file.settings !== 'object') {
    throw new Error('Invalid backup: settings is missing or not an object');
  }
}

function isBackupFile(value: unknown): value is BackupFile {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.schemaVersion === 'number' &&
    Array.isArray(v.events) &&
    typeof v.settings === 'object' &&
    v.settings !== null
  );
}
